import * as React from 'react';

import cx from 'classnames';
import { OrganizationRole } from 'globalTypes';
import { capitalize, isEmpty, omit } from 'lodash';

import AvatarWithTextItem from 'components/AvatarWithTextItem';
import AvatarWithTextItems from 'components/AvatarWithTextItems';
import LoadingIndicator from 'components/LoadingIndicator';
import Profile, { ProfileSection } from 'components/Profile';
import Text from 'components/Text';
// @ts-ignore
import User from 'entities/User';
import UnclaimedGroupProfileHeader from 'groups/components/UnclaimedGroupProfileHeader';
import EventTracking from 'lib/EventTracking';
// @ts-ignore
import { UserProfile } from 'users/components/UsersProfile';

export type GroupMember = {
  uuid: string;
  firstName: string | null;
  lastName: string | null;
  color: string | null;
  initials: string | null;
  roles: OrganizationRole[];
};

export type PartialUnclaimedGroupProfileType = {
  uuid: string;
  className: string;
  initials: string;
};

export type FullUnclaimedGroupProfileType = {
  uuid: string;
  className: string;
  initials: string;
  ownersCount: number;
  owners: GroupMember[];
  membershipsCount: number;
  members: GroupMember[];
};

type Props = {
  partialGroup: PartialUnclaimedGroupProfileType;
  loadedGroup: FullUnclaimedGroupProfileType | null;
  loading: boolean;
  onBackClicked?: () => void;
  onClose: () => void;
  onUserChanged: () => void;
  onUserClicked: (user: UserProfile, role: OrganizationRole) => void;
};

export default class UnclaimedGroupProfile extends React.Component<Props> {
  componentDidMount() {
    EventTracking.trackKey('admin_dashboard.class_list.unclaimed_group_profile.view');
  }

  getGroup() {
    const { partialGroup, loadedGroup } = this.props;
    return loadedGroup || partialGroup;
  }

  getSections = (): Array<ProfileSection> => {
    const { loading, loadedGroup } = this.props;
    const sections: Array<ProfileSection> = [];

    if (loading) {
      return sections;
    }

    if (loadedGroup) {
      const { owners, ownersCount, members, membershipsCount } = loadedGroup;

      sections.push({
        title: 'Class owners',
        children: this.renderGroupMembersList(owners, 'owned', ownersCount),
        count: ownersCount,
      });

      sections.push({
        title: 'Participants',
        children: this.renderGroupMembersList(members, 'joined', membershipsCount),
        count: membershipsCount,
      });
    }

    return sections;
  };

  roleDescriptionForMember(member: GroupMember) {
    const { roles } = member;

    if (roles && !isEmpty(roles)) {
      return capitalize(roles.join(', '));
    } else {
      return 'No role';
    }
  }

  renderAndMoreText = (members: GroupMember[], groupType: 'owned' | 'joined', count: number) => {
    if (count > members.length) {
      const type = groupType === 'owned' ? 'class owners' : 'participants';

      return (
        <Text className="GroupProfile-andMoreText" color="eclipse">
          {`Showing ${members.length} of the ${count} ${type}`}
        </Text>
      );
    }

    return null;
  };

  renderGroupMembersList = (
    members: GroupMember[],
    groupType: 'owned' | 'joined',
    count: number
  ) => {
    if (isEmpty(members)) {
      return null;
    }

    const { onUserClicked, onUserChanged } = this.props;

    return (
      <AvatarWithTextItems>
        {members.map(member => {
          const roleDescription = this.roleDescriptionForMember(member);

          return (
            <AvatarWithTextItem
              subtle
              user={User.fromJSON(member)}
              subtext={roleDescription}
              key={member.uuid}
              onClick={() => onUserClicked(omit(member, 'roles'), member.roles[0])}
            />
          );
        })}
        {this.renderAndMoreText(members, groupType, count)}
      </AvatarWithTextItems>
    );
  };

  renderLoadingIndicator() {
    const { loading } = this.props;

    if (loading) {
      return <LoadingIndicator align="center" size="small" />;
    }
  }

  render() {
    const { onClose, onBackClicked } = this.props;
    const group = this.getGroup();

    const handleSectionVisibilityChange = (uuids: string[]) => {
      if (uuids.includes('Class owners')) {
        EventTracking.trackKey('admin_dashboard.class_list.profile_view.class_owners.expand.click');
      }

      if (uuids.includes('Participants')) {
        EventTracking.trackKey('admin_dashboard.class_list.profile_view.participants.expand.click');
      }
    };

    return (
      <Profile
        header={<UnclaimedGroupProfileHeader group={group} />}
        loadingIndicator={this.renderLoadingIndicator()}
        sections={this.getSections()}
        className={cx('UnclaimedGroupProfile', 'GroupProfile')}
        onBackClicked={onBackClicked}
        onClose={onClose}
        onChange={handleSectionVisibilityChange}
      />
    );
  }
}
